import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const MENTOR = "https://images.unsplash.com/photo-1551836022-4c4c79ecde51?crop=entropy&cs=srgb&fm=jpg&q=85";
const TEAM = "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?crop=entropy&cs=srgb&fm=jpg&q=85";

const values = [
  { k: "Integrity", v: "Doing right even when no one is watching. Every conversation, every contract." },
  { k: "Service", v: "We lead with how we can help — clients, teammates, and community first." },
  { k: "Growth", v: "Constant learning. Personal, professional, and financial — for everyone we touch." },
  { k: "Leadership", v: "We develop people who develop people. Influence multiplied through example." },
  { k: "Family", v: "Families are the unit we protect — including the one we build inside our agency." },
  { k: "Legacy", v: "We measure success in the generations affected, not the policies sold." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-20">
        <div className="overline mb-6">— About Impact Legacy Group</div>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] text-white tracking-tighter leading-[0.95] max-w-5xl">
          A modern agency built on <span className="italic text-[#D4AF37]">service, integrity, and impact.</span>
        </h1>
      </section>

      {/* MISSION SPLIT */}
      <section className="relative py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 relative">
            <div className="relative">
              <img src={MENTOR} alt="Mentorship" className="w-full h-[520px] object-cover grayscale" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/60 to-transparent" />
            </div>
            <div className="absolute -bottom-10 -right-6 hidden md:block">
              <img src={TEAM} alt="Team" className="w-64 h-72 object-cover border-4 border-[#0D0D0D] shadow-2xl" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="overline mb-5">Our Mission</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-8">
              To protect families, develop leaders, and create <span className="italic text-[#D4AF37]">lasting legacies</span> through service, integrity, and financial education.
            </h2>
            <p className="text-white/70 leading-relaxed mb-6">
              At Impact Legacy Group, we are committed to helping families secure their futures while empowering individuals to build rewarding careers with purpose. We believe the right protection — paired with the right people — changes the trajectory of generations.
            </p>
            <Link to="/contact" data-testid="about-cta-contact" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs tracking-[0.22em] uppercase border-b border-[#D4AF37]/50 pb-1 hover:gap-4 transition-all">
              Connect With Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="relative py-28 md:py-40 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-5">— Core Values</div>
              <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight">
                The principles that <span className="italic text-[#D4AF37]">guide every decision.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-white/65 leading-relaxed text-lg">
                These six values are non-negotiable. They define who we hire, how we serve, and what kind of legacy our agency leaves behind.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {values.map((v, i) => (
              <div key={v.k} data-testid={`value-${i}`} className="bg-[#0D0D0D] p-10 hover:bg-[#1A1A1A] transition-colors duration-500 group">
                <div className="flex items-baseline gap-4 mb-5">
                  <span className="font-serif text-3xl text-[#D4AF37]">0{i + 1}</span>
                  <span className="h-px flex-1 bg-white/10 group-hover:bg-[#D4AF37]/40 transition-colors" />
                </div>
                <h3 className="font-serif text-3xl text-white mb-3">{v.k}</h3>
                <p className="text-white/60 leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
